// ABOUTME: Code block component with syntax highlighting.
// ABOUTME: Uses highlight.js for SQL, Python, and JSON syntax highlighting.

import { useMemo } from 'react';
import hljs from 'highlight.js/lib/core';
import sql from 'highlight.js/lib/languages/sql';
import python from 'highlight.js/lib/languages/python';
import json from 'highlight.js/lib/languages/json';
import 'highlight.js/styles/atom-one-dark.css';
import { Copy, Check } from 'lucide-react';
import { useState } from 'react';

// Register languages
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('python', python);
hljs.registerLanguage('json', json);

interface CodeBlockProps {
  code: string;
  language?: 'sql' | 'python' | 'json';
  title?: string;
  showCopy?: boolean;
}

export function CodeBlock({ code, language = 'sql', title, showCopy = true }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  // Use highlight() instead of highlightElement() to avoid security warnings
  // highlight() properly escapes HTML entities in the code
  const highlightedCode = useMemo(() => {
    // Guard against undefined/null code
    if (!code || typeof code !== 'string') {
      return '';
    }
    try {
      const result = hljs.highlight(code, { language, ignoreIllegals: true });
      return result.value;
    } catch {
      // Fallback to escaped plain text if highlighting fails
      return code
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }
  }, [code, language]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-sm overflow-hidden bg-surface-100 border border-surface-200">
      {title && (
        <div className="flex items-center justify-between px-4 py-2 bg-surface-50 border-b border-surface-200">
          <span className="text-xs font-mono text-surface-500 uppercase tracking-wider">
            {title}
          </span>
          {showCopy && (
            <button
              onClick={handleCopy}
              className="p-1 rounded-sm hover:bg-surface-200 text-surface-500 hover:text-surface-800 hover:scale-110 transition-all cursor-pointer"
              aria-label="Copy code"
            >
              {copied ? (
                <Check className="w-4 h-4 text-success" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          )}
        </div>
      )}
      <pre className="p-4 overflow-x-auto bg-surface-50 dark:bg-[#282c34]">
        <code
          className={`language-${language} text-sm font-mono hljs !bg-transparent`}
          dangerouslySetInnerHTML={{ __html: highlightedCode }}
        />
      </pre>
    </div>
  );
}
